import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutDashboard, Building2, Users, Columns3, FileSignature, Briefcase, DollarSign, Flame, Inbox, Rocket } from 'lucide-react';
import { IntelligenceLayout } from './IntelligenceLayout';
import CRMCommandCenter from './CRMCommandCenter';
import CRMCompanies from './CRMCompanies';
import CRMContacts from './CRMContacts';
import CRMPipeline from './CRMPipeline';
import CRMAgreements from './CRMAgreements';
import CRMJobOrders from './CRMJobOrders';
import CRMRevenue from './CRMRevenue';
import CRMOpportunities from './CRMOpportunities';
import OutreachQueue from './OutreachQueue';
import CRMSequences from './CRMSequences';

const TABS = [
  { key: 'command-center', label: 'Command Center', icon: LayoutDashboard, Component: CRMCommandCenter },
  { key: 'opportunities', label: 'Opportunities', icon: Flame, Component: CRMOpportunities },
  { key: 'outreach-queue', label: 'Outreach Queue', icon: Inbox, Component: OutreachQueue },
  { key: 'sequences', label: 'Sequences', icon: Rocket, Component: CRMSequences },
  { key: 'companies', label: 'Companies', icon: Building2, Component: CRMCompanies },
  { key: 'contacts', label: 'Contacts', icon: Users, Component: CRMContacts },
  { key: 'pipeline', label: 'Pipeline', icon: Columns3, Component: CRMPipeline },
  { key: 'agreements', label: 'Agreements', icon: FileSignature, Component: CRMAgreements },
  { key: 'job-orders', label: 'Job Orders', icon: Briefcase, Component: CRMJobOrders },
  { key: 'revenue', label: 'Revenue', icon: DollarSign, Component: CRMRevenue },
];

const DEFAULT_TAB = 'command-center';

export default function CRMHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    TABS.some(t => t.key === requestedTab) ? requestedTab : DEFAULT_TAB
  );
  // Filter params for the target tab (e.g. { scored: 'true' } from a Command
  // Center "View All" click). Passed directly as a prop rather than re-read
  // from the URL inside the child, because setSearchParams' navigation and
  // this component's own setActiveTab don't always land in the same paint —
  // a child reading useSearchParams() at mount could still see the old URL.
  const [navParams, setNavParams] = useState(() => Object.fromEntries(searchParams.entries()));

  // Keep activeTab in sync with the URL — needed because the left sidebar
  // navigates via <Link> (query-string-only change, no remount), so relying
  // on the initial useState value alone left the tab bar stuck on whatever
  // tab was active before a sidebar click.
  useEffect(() => {
    const tab = searchParams.get('tab');
    setActiveTab(TABS.some(t => t.key === tab) ? tab : DEFAULT_TAB);
  }, [searchParams]);

  const handleTabClick = (key, extraParams = {}) => {
    setActiveTab(key);
    setNavParams(extraParams);
    setSearchParams({ tab: key, ...extraParams });
  };

  const ActiveComponent = TABS.find(t => t.key === activeTab)?.Component || CRMCommandCenter;

  return (
    <IntelligenceLayout title="CRM">
      <div className="border-b border-gray-200 bg-white px-6 pt-4 sticky top-0 z-10" data-testid="crm-hub-tabs">
        <div className="flex items-center gap-1 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => handleTabClick(tab.key)}
              data-testid={`crm-tab-${tab.key}`}
              className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium rounded-t-lg border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'text-[#0A1F44] border-[#D4AF37] bg-[#fdf9ef]'
                  : 'text-gray-500 border-transparent hover:text-[#0A1F44] hover:bg-gray-50'
              }`}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <ActiveComponent embedded onNavigateTab={handleTabClick} navParams={navParams} />
    </IntelligenceLayout>
  );
}
